import useHasAccess from "@hooks/useHasAccess"

import ListItemDivider from "@molecules/ListItem/ListItemDivider/ListItemDivider"
import ToggleListItem from "@molecules/ListItem/ToggleListItem/ToggleListItem"
import { ListItemStack } from "@molecules/index"

import { useCRMConfigMutation, useCRMConfigQuery } from "@pages/Settings/SettingsCRM/SettingsCRM.store"
import { CRMConfigPayload } from "@pages/Settings/SettingsCRM/SettingsCRM.types"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

export default function CRMSyncSettingsSection() {
    const configQuery = useCRMConfigQuery()
    const { mutate, isPending } = useCRMConfigMutation()
    const { hasPermission } = useHasAccess()

    const canEditSettings = hasPermission("settings_edit_permission", PERMISSION_LEVEL.FULL)
    const isToggleDisabled = isPending || !canEditSettings

    const config = configQuery.data
    const integrationName = config?.crm_integration_name || "the CRM"
    const isObjectToggleDisabled = isToggleDisabled || !config?.crm_sync_enabled
    const contactsCaption = config?.crm_flat_client_sync
        ? `Keep client contacts up to date as ${integrationName} contacts.`
        : `Keep client and service location contacts up to date as ${integrationName} contacts.`

    // Send every sync setting, since the answer replaces the cached configuration wholesale.
    const updateConfig = (changes: Partial<CRMConfigPayload>) => {
        if (config) {
            mutate({
                crm_sync_enabled: config.crm_sync_enabled,
                crm_flat_client_sync: config.crm_flat_client_sync,
                crm_contact_sync_enabled: config.crm_contact_sync_enabled,
                crm_activity_sync_enabled: config.crm_activity_sync_enabled,
                ...changes,
            })
        }
    }

    return (
        <ListItemStack>
            <ToggleListItem
                label={`Sync with ${integrationName}`}
                caption="Exports and imports run only while this is on."
                checked={!!config?.crm_sync_enabled}
                onCheckedChange={(checked) => updateConfig({ crm_sync_enabled: checked })}
                isLoading={configQuery.isLoading}
                isDisabled={isToggleDisabled}
            />
            <ListItemDivider />
            <ToggleListItem
                label="Sync service locations"
                caption={`Create a company in ${integrationName} for each service location. Off means only clients and client contacts are synced.`}
                checked={!config?.crm_flat_client_sync}
                onCheckedChange={(checked) => updateConfig({ crm_flat_client_sync: !checked })}
                isLoading={configQuery.isLoading}
                isDisabled={isObjectToggleDisabled}
            />
            <ListItemDivider />
            <ToggleListItem
                label="Sync contacts"
                caption={contactsCaption}
                checked={!!config?.crm_contact_sync_enabled}
                onCheckedChange={(checked) => updateConfig({ crm_contact_sync_enabled: checked })}
                isLoading={configQuery.isLoading}
                isDisabled={isObjectToggleDisabled}
            />
            <ListItemDivider />
            <ToggleListItem
                label="Sync activity notes"
                caption={`Add a note to the ${integrationName} company for each job, estimate and invoice event.`}
                checked={!!config?.crm_activity_sync_enabled}
                onCheckedChange={(checked) => updateConfig({ crm_activity_sync_enabled: checked })}
                isLoading={configQuery.isLoading}
                isDisabled={isObjectToggleDisabled}
            />
        </ListItemStack>
    )
}
